import axios from 'axios';
import Button from 'react-bootstrap/Button';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';
import { InvoicePageDouble, renderInvoicePdf } from '../InvoiceTemplate';
import '../../assets/invoice-classic.css';

const PAGE_SIZE = 100;

const STATUS_TABS = [
  { value: 'pending', label: 'Pending', variant: 'danger' },
  { value: 'approved', label: 'Approved', variant: 'success' },
  { value: 'all', label: 'All Orders', variant: 'secondary' },
];

const DELIVERY_FILTERS = [
  { value: '', label: 'Any delivery status' },
  { value: 'yes', label: 'Delivered' },
  { value: 'no', label: 'Not Delivered' },
];

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        orders: action.payload.orders || [],
        promotion: action.payload.promotion || [],
        total: action.payload.total || 0,
        pages: action.payload.pages || 1,
        totalPrice: action.payload.totalPrice || 0,
        totalPoint: action.payload.totalPoint || 0,
        totalItems: action.payload.totalItems || 0,
        pendingCount: action.payload.pendingCount || 0,
        approvedCount: action.payload.approvedCount || 0,
        periodCounts: action.payload.periodCounts || { today: 0, week: 0, month: 0, total: 0 },
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function OrderListScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const type = sp.get('type') || 'pending';
  const page = Math.max(parseInt(sp.get('page')) || 1, 1);
  const qSearch = sp.get('search') || '';
  const qStart = sp.get('startDate') || '';
  const qEnd = sp.get('endDate') || '';
  const qDelivered = sp.get('delivered') || '';

  const [{
    loading, error, orders, promotion, total, pages,
    pendingCount, approvedCount, periodCounts,
  }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    orders: [],
    promotion: [],
    total: 0,
    pages: 1,
    totalPrice: 0,
    totalPoint: 0,
    totalItems: 0,
    pendingCount: 0,
    approvedCount: 0,
    periodCounts: { today: 0, week: 0, month: 0, total: 0 },
  });

  const fetchData = useCallback(async () => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const { data } = await axios.post(
        `${api}/api/orders`,
        {
          type,
          page,
          pageSize: PAGE_SIZE,
          search: qSearch,
          startDate: qStart,
          endDate: qEnd,
          delivered: qDelivered,
        },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [type, page, qSearch, qStart, qEnd, qDelivered, userInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPosition = useCallback((username) => {
    if (!username || !promotion || promotion.length === 0) return '';
    const sorted = [...promotion].sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );
    const found = sorted.find((p) => p.username === username);
    return found ? found.position_name : '';
  }, [promotion]);

  // Delete confirmation modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Invoice PDF generation state
  const [pdfOrder, setPdfOrder] = useState(null);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null);

  const downloadInvoice = useCallback(
    async (orderId) => {
      if (pdfDownloadingId) return;
      setPdfDownloadingId(orderId);
      try {
        const { data } = await axios.get(`${api}/api/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        setPdfOrder(data);
        // Wait two animation frames so the hidden invoice mounts and lays out before capture.
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );
        const node = document.getElementById('invc-page-hidden');
        await renderInvoicePdf(node, `Invoice_${data._id}.pdf`);
        toast.success('Invoice downloaded.');
      } catch (err) {
        toast.error(getError(err) || 'Failed to download invoice.');
      } finally {
        setPdfOrder(null);
        setPdfDownloadingId(null);
      }
    },
    [pdfDownloadingId, userInfo]
  );

  const deleteOrder = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await axios.delete(`${api}/api/orders/${deleteTarget._id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      toast.success('Order deleted.');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setDeleting(false);
    }
  };

  // Filter form local state (applied on Search click)
  const [fSearch, setFSearch] = useState(qSearch);
  const [fStart, setFStart] = useState(qStart);
  const [fEnd, setFEnd] = useState(qEnd);
  const [fDelivered, setFDelivered] = useState(qDelivered);

  useEffect(() => {
    setFSearch(qSearch);
    setFStart(qStart);
    setFEnd(qEnd);
    setFDelivered(qDelivered);
  }, [qSearch, qStart, qEnd, qDelivered]);

  const buildPath = (overrides) => {
    const merged = {
      type, page: 1, search: qSearch, startDate: qStart, endDate: qEnd, delivered: qDelivered,
      ...overrides,
    };
    const next = new URLSearchParams();
    if (merged.type && merged.type !== 'pending') next.set('type', merged.type);
    if (merged.page && merged.page !== 1) next.set('page', merged.page);
    if (merged.search) next.set('search', merged.search);
    if (merged.startDate) next.set('startDate', merged.startDate);
    if (merged.endDate) next.set('endDate', merged.endDate);
    if (merged.delivered) next.set('delivered', merged.delivered);
    const q = next.toString();
    return `${location.pathname}${q ? `?${q}` : ''}`;
  };

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    if (fStart && fEnd && fStart > fEnd) {
      toast.error('Start date must be before end date.');
      return;
    }
    navigate(buildPath({
      page: 1,
      search: fSearch.trim(),
      startDate: fStart,
      endDate: fEnd,
      delivered: fDelivered,
    }));
  };

  const resetFilters = () => {
    setFSearch('');
    setFStart('');
    setFEnd('');
    setFDelivered('');
    navigate(buildPath({ page: 1, search: '', startDate: '', endDate: '', delivered: '' }));
  };

  const switchTab = (value) => {
    if (value === type) return;
    navigate(buildPath({ type: value, page: 1 }));
  };

  const goToPage = (p) => {
    if (p < 1 || p > pages || p === page) return;
    navigate(buildPath({ page: p }));
  };

  const hasActiveFilter = Boolean(qSearch || qStart || qEnd || qDelivered);

  const pageNumbers = useMemo(() => {
    const nums = [];
    const windowSize = 2;
    const start = Math.max(1, page - windowSize);
    const end = Math.min(pages, page + windowSize);
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, pages]);

  const fmtNum = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '0';
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const fmtDateTime = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (isNaN(d)) return String(s).substring(0, 10);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (isNaN(d)) return String(s).substring(0, 10);
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
    });
  };

  const firstIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIndex = Math.min(page * PAGE_SIZE, total);

  return (
    <div>
      <Helmet>
        <title>Manage Orders</title>
      </Helmet>
      <Container fluid="lg" className="mb-4 pt-3">
        <div className="mb-3">
          <h4 className="mb-1 fw-semibold">Manage Orders</h4>
          <div className="text-muted small">Review, filter, approve, and export customer orders.</div>
        </div>

        {/* Summary — simple 6-stat strip */}
        <Row className="g-2 mb-3">
          {[
            { label: 'Total', value: periodCounts?.total || 0, color: 'text-dark' },
            { label: 'Pending', value: pendingCount, color: 'text-danger' },
            { label: 'Approved', value: approvedCount, color: 'text-success' },
            { label: 'Today', value: periodCounts?.today || 0, color: 'text-primary' },
            { label: 'This Week', value: periodCounts?.week || 0, color: 'text-primary' },
            { label: 'This Month', value: periodCounts?.month || 0, color: 'text-primary' },
          ].map((s) => (
            <Col xs={6} md={4} lg={2} key={s.label}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-3">
                  <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                    {s.label}
                  </div>
                  <div className={`h4 mb-0 fw-semibold ${s.color}`}>
                    {loading ? '—' : s.value.toLocaleString()}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Status Tabs + Inline Search */}
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body className="py-3">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {STATUS_TABS.map((t) => {
                const isActive = type === t.value;
                const count =
                  t.value === 'pending' ? pendingCount :
                  t.value === 'approved' ? approvedCount :
                  pendingCount + approvedCount;
                return (
                  <Button
                    key={t.value}
                    size="sm"
                    variant={isActive ? t.variant : `outline-${t.variant}`}
                    onClick={() => switchTab(t.value)}
                    disabled={loading}
                  >
                    {t.label}
                    <Badge bg={isActive ? 'light' : t.variant} text={isActive ? 'dark' : undefined} className="ms-2">
                      {count.toLocaleString()}
                    </Badge>
                  </Button>
                );
              })}
            </div>
            <Form onSubmit={applyFilters}>
              <Row className="g-2 align-items-center">
                <Col xs={12} md={4}>
                  <Form.Control
                    size="sm"
                    placeholder="Search by customer username"
                    value={fSearch}
                    onChange={(e) => setFSearch(e.target.value)}
                    autoComplete="off"
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={fStart}
                    onChange={(e) => setFStart(e.target.value)}
                    max={fEnd || undefined}
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={fEnd}
                    onChange={(e) => setFEnd(e.target.value)}
                    min={fStart || undefined}
                  />
                </Col>
                <Col xs={8} md={2}>
                  <Form.Select
                    size="sm"
                    value={fDelivered}
                    onChange={(e) => setFDelivered(e.target.value)}
                  >
                    {DELIVERY_FILTERS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={4} md={2} className="d-flex gap-2">
                  <Button type="submit" variant="dark" size="sm" className="flex-grow-1" disabled={loading}>
                    Search
                  </Button>
                  {hasActiveFilter && (
                    <Button type="button" variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                      ✕
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Orders Table */}
        <Card className="shadow-sm">
          <Card.Header className="bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="fw-bold">
              {STATUS_TABS.find((t) => t.value === type)?.label || 'Orders'}
            </div>
            <div className="small text-muted">
              {loading ? 'Loading...' : total > 0
                ? `Showing ${firstIndex}-${lastIndex} of ${total.toLocaleString()}`
                : 'No orders'}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="p-4 text-center"><LoadingBox /></div>
            ) : error ? (
              <div className="p-3"><MessageBox variant="danger">{error}</MessageBox></div>
            ) : orders.length === 0 ? (
              <div className="p-5 text-center text-muted">
                <div style={{ fontSize: '2rem' }}>📭</div>
                <div className="mt-2">No orders match your filters.</div>
                {hasActiveFilter && (
                  <Button variant="link" onClick={resetFilters}>Clear filters</Button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>Order</th>
                      <th>Customer</th>
                      <th className="text-center">Items</th>
                      <th className="text-end">Point</th>
                      <th className="text-end">Amount</th>
                      <th>Payment</th>
                      <th>Delivery</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => {
                      const items = order.orderItems || [];
                      const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
                      const preview = items.slice(0, 3).map((i) => `${i.name} × ${i.quantity}`).join('\n');
                      const more = items.length > 3 ? `\n+${items.length - 3} more` : '';
                      return (
                        <tr key={order._id}>
                          <td className="text-muted">{firstIndex + idx}</td>
                          <td>
                            <div style={{ whiteSpace: 'nowrap' }}>
                              <code className="small" title={order._id}>#{order._id.slice(-6)}</code>
                            </div>
                            <div className="small text-muted" title={new Date(order.createdAt).toISOString()} style={{ whiteSpace: 'nowrap' }}>
                              {fmtDateTime(order.createdAt)}
                            </div>
                          </td>
                          <td>
                            {order.user ? (
                              <>
                                <div className="fw-semibold">{order.user.name || order.user.username}</div>
                                <div className="small text-muted">
                                  @{order.user.username}
                                  {getPosition(order.user.username) && (
                                    <Badge bg="light" text="dark" className="ms-1 border">
                                      {getPosition(order.user.username)}
                                    </Badge>
                                  )}
                                </div>
                              </>
                            ) : (
                              <span className="text-danger">Deleted user</span>
                            )}
                          </td>
                          <td className="text-center">
                            <span title={preview + more} style={{ cursor: 'help' }}>
                              <Badge bg="light" text="dark" className="border">
                                {totalQty}
                              </Badge>
                            </span>
                          </td>
                          <td className="text-end">{fmtNum(order.totalPoint)}</td>
                          <td className="text-end fw-bold">{fmtNum(order.totalPrice)} <span className="small text-muted">TK</span></td>
                          <td>
                            {order.isPaid ? (
                              <>
                                <Badge bg="success">Paid</Badge>
                                <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                                  {fmtDate(order.paidAt)}
                                </div>
                              </>
                            ) : (
                              <Badge bg="warning" text="dark">Pending</Badge>
                            )}
                          </td>
                          <td>
                            {order.isDelivered ? (
                              <>
                                <Badge bg="success">Delivered</Badge>
                                <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                                  {fmtDate(order.deliveredAt)}
                                </div>
                              </>
                            ) : (
                              <Badge bg="secondary">Not delivered</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              <Link
                                to={`/order/${order._id}`}
                                className="btn btn-sm btn-outline-primary"
                                title="View order details"
                              >
                                Details
                              </Link>
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => downloadInvoice(order._id)}
                                disabled={pdfDownloadingId === order._id}
                                title="Download classic invoice PDF"
                              >
                                {pdfDownloadingId === order._id ? (
                                  <>
                                    <Spinner animation="border" size="sm" /> …
                                  </>
                                ) : (
                                  'Invoice'
                                )}
                              </Button>
                              <Link
                                className="btn btn-sm btn-outline-success"
                                target="_blank"
                                rel="noopener noreferrer"
                                to={`/invoice-new/${order._id}`}
                                title="Open new professional invoice in a new tab"
                              >
                                New Invoice
                              </Link>
                              {items.length > 0 && (
                                <a
                                  className="btn btn-sm btn-outline-secondary"
                                  href={`${api}/api/invoices/order/${order._id}/vendors.pdf?token=${encodeURIComponent(userInfo.token)}&download=1`}
                                  title="One PDF, one invoice page per product — send each page to that product's vendor"
                                >
                                  Vendor PDF ({items.length} {items.length === 1 ? 'page' : 'pages'})
                                </a>
                              )}
                              {userInfo.isAdmin && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => setDeleteTarget(order)}
                                  disabled={deleting}
                                  title="Delete this order"
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
          {!loading && !error && orders.length > 0 && pages > 1 && (
            <Card.Footer className="bg-white">
              <nav aria-label="Order pagination">
                <ul className="pagination pagination-sm justify-content-center mb-0 flex-wrap">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(1)} disabled={page === 1}>« First</button>
                  </li>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page - 1)} disabled={page === 1}>‹ Prev</button>
                  </li>
                  {pageNumbers[0] > 1 && (
                    <li className="page-item disabled"><span className="page-link">…</span></li>
                  )}
                  {pageNumbers.map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                    </li>
                  ))}
                  {pageNumbers[pageNumbers.length - 1] < pages && (
                    <li className="page-item disabled"><span className="page-link">…</span></li>
                  )}
                  <li className={`page-item ${page === pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page + 1)} disabled={page === pages}>Next ›</button>
                  </li>
                  <li className={`page-item ${page === pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(pages)} disabled={page === pages}>Last »</button>
                  </li>
                </ul>
                <div className="text-center small text-muted mt-2">
                  Page {page} of {pages}
                </div>
              </nav>
            </Card.Footer>
          )}
        </Card>

        {/* Delete confirmation modal */}
        <Modal
          show={Boolean(deleteTarget)}
          onHide={() => !deleting && setDeleteTarget(null)}
          backdrop="static"
          keyboard={!deleting}
          centered
        >
          <Modal.Header closeButton={!deleting}>
            <Modal.Title>Delete this order?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              This will permanently remove the order from the database. This cannot be undone.
            </p>
            {deleteTarget && (
              <div className="border rounded p-2 bg-light small">
                <div><strong>Order:</strong> #{deleteTarget._id.slice(-6)}</div>
                <div><strong>Customer:</strong> {deleteTarget.user?.username || 'DELETED USER'}</div>
                <div><strong>Amount:</strong> {fmtNum(deleteTarget.totalPrice)} TK</div>
                <div><strong>Date:</strong> {fmtDate(deleteTarget.createdAt)}</div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteOrder} disabled={deleting}>
              {deleting ? <><Spinner animation="border" size="sm" /> Deleting...</> : 'Yes, delete'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Hidden offscreen invoice used only for PDF capture */}
        {pdfOrder && (
          <div
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: '-99999px',
              top: 0,
              width: '210mm',
              pointerEvents: 'none',
              zIndex: -1,
            }}
          >
            <InvoicePageDouble order={pdfOrder} id="invc-page-hidden" />
          </div>
        )}
      </Container>
    </div>
  );
}
